import React, { useEffect, useState } from 'react';
import { User, List, LogOut, MessageSquare, Settings, Shield, FileText, Bell } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { Button } from './ui/button';

interface LayoutProps {
  children: React.ReactNode;
  currentPage: 'candidates' | 'evaluator' | 'dashboard' | 'admin' | 'discussion' | 'settings';
  onPageChange: (page: LayoutProps['currentPage']) => void;
  userType: 'candidate' | 'evaluator' | null;
  onLogout: () => void;
  isAdmin?: boolean;
}

export default function Layout({
  children,
  currentPage,
  onPageChange,
  userType,
  onLogout,
  isAdmin = false
}: LayoutProps) {
  const [notifCount, setNotifCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const loadNotifCount = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (cancelled || !user?.email) return;

        const { count, error } = await supabase
          .from('notification_queue')
          .select('*', { count: 'exact', head: true })
          .eq('recipient_email', user.email)
          .is('read_at', null);

        if (cancelled) return;
        if (!error && count) {
          setNotifCount(count);
        } else {
          setNotifCount(0);
        }
      } catch {
        // Silently fail
      }
    };
    loadNotifCount();
    const interval = setInterval(loadNotifCount, 30000);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    onLogout();
  };

  const navItems: { page: LayoutProps['currentPage']; label: string; icon: React.ReactNode; show: boolean }[] = [
    { page: userType === 'candidate' ? 'candidates' : 'evaluator', label: 'マイページ', icon: <User className="h-4 w-4" />, show: true },
    { page: 'dashboard', label: 'ダッシュボード', icon: <List className="h-4 w-4" />, show: true },
    { page: 'discussion', label: 'ディスカッション', icon: (
      <span className="relative">
        <MessageSquare className="h-4 w-4" />
        {notifCount > 0 && (
          <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[9px] font-bold rounded-full min-w-[14px] h-[14px] flex items-center justify-center px-0.5">
            {notifCount > 99 ? '99+' : notifCount}
          </span>
        )}
      </span>
    ), show: true },
    { page: 'settings', label: '設定', icon: <Settings className="h-4 w-4" />, show: true },
    { page: 'admin', label: '管理者', icon: <Shield className="h-4 w-4" />, show: isAdmin },
  ];

  const isActive = (page: string) => {
    if (page === 'candidates' || page === 'evaluator') {
      return currentPage === 'candidates' || currentPage === 'evaluator';
    }
    return currentPage === page;
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <nav className="border-b bg-card/50 backdrop-blur supports-[backdrop-filter]:bg-card/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center cursor-pointer" onClick={() => onPageChange('dashboard')}>
              <FileText className="h-7 w-7 text-primary mr-2" />
              <h1 className="text-lg font-bold text-foreground">ToRA-SEED</h1>
            </div>

            <div className="flex items-center space-x-1">
              {navItems.filter(n => n.show).map((item) => (
                <Button
                  key={item.page}
                  variant="ghost"
                  size="sm"
                  onClick={() => onPageChange(item.page)}
                  aria-label={item.label}
                  aria-current={isActive(item.page) ? 'page' : undefined}
                  className={isActive(item.page) ? 'bg-accent text-accent-foreground' : ''}
                >
                  {item.icon}
                  <span className="hidden sm:inline ml-1">{item.label}</span>
                </Button>
              ))}
              <Button variant="ghost" size="sm" onClick={handleSignOut} aria-label="ログアウト">
                <LogOut className="h-4 w-4" />
                <span className="hidden sm:inline ml-1">ログアウト</span>
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8 space-y-6 flex-1 w-full">
        {children}
      </main>

      <footer className="border-t">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex flex-col sm:flex-row items-end sm:items-center sm:justify-between gap-1">
          <p className="text-xs text-muted-foreground/70">Produced by 株式会社amiac one</p>
          <p className="text-right text-xs text-muted-foreground">ToRA-SEED - 群馬大学・千葉大学・筑波大学</p>
        </div>
      </footer>
    </div>
  );
}
